import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useClinicBasePath } from '@/hooks/useClinicBasePath';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  Text,
  TextField,
  RadioButton,
  Checkbox,
  Button,
  Banner,
  Box,
  Divider,
  Modal,
  List,
  Link,
} from '@shopify/polaris';
import { clinicPortalApi } from '@/services';
import type { CreateOrderResponse } from '@/services/clinicPortal';
import type { Patient } from '@/types';

interface OrderData {
  patientId: string;
  productId: string;
  productName: string;
  productSku: string;
  variantId: string;
  size: string;
  weeklyRate: number;
  surgeryDate: string;
  rentalWeeks: number;
  isBilateral: boolean;
  bilateralPrice: number;
  addOns: { name: string; sku: string; price: number; quantity: number }[];
  wrap?: {
    productId: string;
    productTitle: string;
    variantId?: string;
    variantTitle?: string;
    sku?: string;
  };
  subtotal: number;
}

export default function ClinicCheckout() {
  const navigate = useNavigate();
  const base = useClinicBasePath();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId') || '';

  const [orderData, setOrderData] = useState<OrderData | null>(null);
  // Default to HealFaster fulfilment. The "Fulfilled by Clinic" option is only
  // surfaced for consignment clinics that hold local inventory (see profile gate).
  const [fulfillmentType, setFulfillmentType] = useState<'clinic' | 'healfaster'>('healfaster');
  // Where to ship when HealFaster fulfils. Defaults to patient address; some
  // patients prefer to pick up from the clinic instead.
  const [shipDestination, setShipDestination] = useState<'patient' | 'clinic'>('patient');

  // Fetch the current clinic's profile to know whether to surface the
  // "Fulfilled by Clinic" option.
  const { data: profile } = useQuery({
    queryKey: ['clinic.profile'],
    queryFn: () => clinicPortalApi.getProfile(),
  });
  const showClinicFulfilOption = profile?.isConsignmentClinic === true;
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  // Load order data from sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem('clinicOrderData');
    if (stored) {
      try {
        setOrderData(JSON.parse(stored));
      } catch {
        // Invalid data
      }
    }
  }, []);

  // Fetch patient details
  const { data: patientsResponse } = useQuery({
    queryKey: ['clinic-patients', 1, ''],
    queryFn: () => clinicPortalApi.getPatients({ page: 1, limit: 500 }),
  });

  const patient: Patient | undefined = patientsResponse?.data?.find(
    (p: Patient) => p.id === patientId,
  );

  const createOrderMutation = useMutation({
    mutationFn: (data: any) => clinicPortalApi.createOrder(data),
    onSuccess: (result: CreateOrderResponse) => {
      sessionStorage.removeItem('clinicOrderData');
      if (result.status === 'pending_payment' && result.invoiceUrl) {
        // Redirect to Shopify Checkout for payment
        window.location.href = result.invoiceUrl;
      } else {
        // $0 consignment order completed — go to orders list
        navigate(`${base}/orders`);
      }
    },
  });

  const handlePlaceOrder = useCallback(() => {
    if (!orderData || !agreedToTerms) return;

    createOrderMutation.mutate({
      patientId: orderData.patientId,
      productId: orderData.productId,
      productName: orderData.productName,
      productSku: orderData.productSku,
      variantId: orderData.variantId,
      size: orderData.size,
      weeklyRate: orderData.weeklyRate,
      surgeryDate: orderData.surgeryDate,
      rentalWeeks: orderData.rentalWeeks,
      isBilateral: orderData.isBilateral,
      bilateralPrice: orderData.bilateralPrice,
      isClinicFulfil: fulfillmentType === 'clinic',
      shippingAddressType: shipDestination,
      specialInstructions,
      addOns: orderData.addOns,
      wrap: orderData.wrap,
    });
  }, [orderData, agreedToTerms, fulfillmentType, shipDestination, specialInstructions, createOrderMutation]);

  if (!orderData) {
    return (
      <Page title="Checkout" backAction={{ content: 'Back', onAction: () => navigate(-1) }}>
        <Banner tone="warning">
          <p>No order data found. Please start the order process from the patient list.</p>
        </Banner>
      </Page>
    );
  }

  return (
    <Page
      title="Create Order"
      subtitle="Checkout"
      backAction={{ content: 'Back', onAction: () => navigate(-1) }}
    >
      <InlineGrid columns={{ xs: 1, md: '2fr 1fr' }} gap="400">
        {/* Left column */}
        <BlockStack gap="400">
          {createOrderMutation.isError && (
            <Banner tone="critical">
              <p>Failed to place order. Please try again.</p>
            </Banner>
          )}

          {/* Fulfillment Type — only surfaced for consignment clinics. Everyone
              else has HealFaster fulfilment locked in. */}
          {showClinicFulfilOption ? (
            <Card>
              <Text as="h3" variant="headingMd">Checkout</Text>
              <Box paddingBlockStart="300">
                <BlockStack gap="200">
                  <RadioButton
                    label="Fulfilled by HealFaster"
                    checked={fulfillmentType === 'healfaster'}
                    id="fulfil-healfaster"
                    name="fulfillment"
                    onChange={() => setFulfillmentType('healfaster')}
                  />
                  <RadioButton
                    label="Fulfilled by Clinic (consignment inventory)"
                    checked={fulfillmentType === 'clinic'}
                    id="fulfil-clinic"
                    name="fulfillment"
                    onChange={() => setFulfillmentType('clinic')}
                  />
                </BlockStack>
              </Box>
            </Card>
          ) : null}

          {/* Patient Address */}
          {patient && (
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">
                  {patient.firstName} {patient.lastName}
                </Text>
                {patient.streetAddress && (
                  <BlockStack gap="100">
                    <Text as="p">{patient.streetAddress}</Text>
                    <Text as="p">
                      {[patient.city, patient.province].filter(Boolean).join(', ')}
                    </Text>
                    <Text as="p">{patient.postalCode}, Canada</Text>
                    {patient.phone && <Text as="p">Telephone: {patient.phone}</Text>}
                  </BlockStack>
                )}
                {fulfillmentType === 'healfaster' && (
                  <Box paddingBlockStart="200">
                    <BlockStack gap="100">
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        Ship to
                      </Text>
                      <RadioButton
                        label="Patient address"
                        helpText="Ship the rental directly to the patient."
                        checked={shipDestination === 'patient'}
                        id="ship-patient"
                        name="shipDestination"
                        onChange={() => setShipDestination('patient')}
                        disabled={!patient.streetAddress}
                      />
                      <RadioButton
                        label="Clinic address (patient picks up at clinic)"
                        helpText="Ship to the clinic; the patient will collect it on-site."
                        checked={shipDestination === 'clinic'}
                        id="ship-clinic"
                        name="shipDestination"
                        onChange={() => setShipDestination('clinic')}
                      />
                    </BlockStack>
                  </Box>
                )}
              </BlockStack>
            </Card>
          )}

          {/* Shipping Method */}
          {fulfillmentType === 'healfaster' && (
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd" tone="critical">Shipping Method *</Text>
                <Text as="p" tone="subdued">
                  Shipping will be arranged by HealFaster team before surgery date.
                </Text>
              </BlockStack>
            </Card>
          )}

          {fulfillmentType === 'clinic' && (
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">Shipping Method</Text>
                <Text as="p" tone="subdued">No shipping method required for clinic-fulfilled orders.</Text>
              </BlockStack>
            </Card>
          )}

          {/* Special Instructions */}
          <Card>
            <TextField
              label="Special Instructions"
              value={specialInstructions}
              onChange={setSpecialInstructions}
              multiline={4}
              autoComplete="off"
            />
          </Card>
        </BlockStack>

        {/* Right column — Order Summary */}
        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingMd">Order Summary</Text>
            <Divider />

            <InlineStack align="space-between">
              <Text as="span">{orderData.productName}{orderData.size ? ` - ${orderData.size}` : ''}</Text>
            </InlineStack>
            <InlineStack align="space-between">
              <Text as="span" tone="subdued">
                {orderData.rentalWeeks} weeks x ${orderData.weeklyRate.toFixed(2)}
              </Text>
              <Text as="span">${(orderData.weeklyRate * orderData.rentalWeeks).toFixed(2)}</Text>
            </InlineStack>

            {orderData.isBilateral && (
              <InlineStack align="space-between">
                <Text as="span" tone="subdued">Bilateral</Text>
                <Text as="span">${orderData.bilateralPrice.toFixed(2)}</Text>
              </InlineStack>
            )}

            {orderData.wrap && (
              <InlineStack align="space-between">
                <Text as="span" tone="subdued">
                  Wrap: {orderData.wrap.productTitle}
                  {orderData.wrap.variantTitle ? ` — ${orderData.wrap.variantTitle}` : ''}
                </Text>
                <Text as="span">Included</Text>
              </InlineStack>
            )}

            {orderData.addOns.map((addOn) => (
              <InlineStack key={addOn.sku} align="space-between">
                <Text as="span" tone="subdued">{addOn.name}</Text>
                <Text as="span">${addOn.price.toFixed(2)}</Text>
              </InlineStack>
            ))}

            <Divider />

            <InlineStack align="space-between">
              <Text as="span">Subtotal</Text>
              <Text as="span">${orderData.subtotal.toFixed(2)} CAD</Text>
            </InlineStack>
            <InlineStack align="space-between">
              <Text as="span" fontWeight="bold">Total</Text>
              <Text as="span" fontWeight="bold">${orderData.subtotal.toFixed(2)} CAD</Text>
            </InlineStack>

            <Divider />

            <Checkbox
              label={
                <Text as="span">
                  I have read and agree to the{' '}
                  <Link onClick={() => setTermsModalOpen(true)}>Terms & Conditions</Link>{' '}
                  <Text as="span" tone="critical">*</Text>
                </Text>
              }
              checked={agreedToTerms}
              onChange={setAgreedToTerms}
            />

            <Button
              variant="primary"
              onClick={handlePlaceOrder}
              loading={createOrderMutation.isPending}
              disabled={!agreedToTerms}
              fullWidth
              size="large"
            >
              Place Order
            </Button>
          </BlockStack>
        </Card>
      </InlineGrid>

      <Modal
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        title="Game Ready Rental Agreement — Terms & Conditions"
        primaryAction={{
          content: 'I Agree',
          onAction: () => {
            setAgreedToTerms(true);
            setTermsModalOpen(false);
          },
        }}
        secondaryActions={[{ content: 'Close', onAction: () => setTermsModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <BlockStack gap="200">
              <Text as="h3" variant="headingMd">1. Rental Fees</Text>
              <List type="bullet">
                <List.Item>Weekly rental fee: $300.00 CAD per week + applicable tax</List.Item>
                <List.Item>Minimum rental period: 2 weeks</List.Item>
                <List.Item>Bilateral rental option: $300.00 CAD one-time charge</List.Item>
              </List>
            </BlockStack>

            <BlockStack gap="200">
              <Text as="h3" variant="headingMd">
                2. Authorization and Notice of Financial Responsibility
              </Text>
              <Text as="p">
                Any abuse or damage which incurs repair fee(s) will be charged to the renter in
                accordance with this rental agreement. A late fee of $50/day will be incurred past
                the rental end date. If the product is not returned within 5 business days and no
                notification of rental extension has been given, a one-time charge of $6,000.00
                will be applied to the credit card on file.
              </Text>
            </BlockStack>

            <BlockStack gap="200">
              <Text as="h3" variant="headingMd">
                3. Patient / Responsible Party Acknowledgment
              </Text>
              <Text as="p">
                By accepting these terms, the renter agrees to the fees set forth above and
                authorizes Heal Faster Canada to charge the credit card on file for the charges
                specified, as well as any subsequent charges that may be incurred as detailed
                above.
              </Text>
            </BlockStack>

            <BlockStack gap="200">
              <Text as="h3" variant="headingMd">
                4. Global Positioning System ("GPS") Acknowledgment
              </Text>
              <Text as="p">
                The renter acknowledges that they have reviewed and understood the Heal Faster
                Canada GPS Policy and further understands and agrees that:
              </Text>
              <List type="number">
                <List.Item>
                  The rental unit is equipped with a GPS for asset tracking and management
                  purposes.
                </List.Item>
                <List.Item>
                  The GPS can identify the location of the rental unit at any given time and that
                  this information is used solely for tracking the rental unit.
                </List.Item>
                <List.Item>
                  The renter authorizes Heal Faster to use GPS capabilities for the purposes set
                  out in the GPS policy, including to locate the rental unit.
                </List.Item>
                <List.Item>
                  Information gathered by the GPS may be used to recover the rental unit and in
                  any court proceedings in connection thereto.
                </List.Item>
              </List>
            </BlockStack>

            <Text as="p" tone="subdued">
              For questions or to submit return notifications, contact{' '}
              <Link url="mailto:support@healfaster.ca">support@healfaster.ca</Link>.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
